// Staff password policy, mirrored from the backend (internal/staffverify/password.go).
// The backend is the authority — it re-checks every rule — but a form that only learns
// the rules from a rejected request is a bad form, so the same policy lives here too.
// Change one, change the other.

export const PASSWORD_MIN_LENGTH = 12;

export const PASSWORD_HINT =
  "Дор хаяж 12 тэмдэгт, том, жижиг үсэг, тоо, тусгай тэмдэгт агуулсан байх ёстой. Нэвтрэх нэр, утас, төрсөн огноо агуулж болохгүй.";

export interface PasswordRule {
  id: string;
  label: string;
  test: (value: string) => boolean;
}

/**
 * The five hard character rules of the policy, as individual predicates so a
 * live checklist can flip each one green the moment the typed value satisfies
 * it. These mirror the character checks in {@link validatePassword} (and the
 * backend `internal/staffverify/password.go`) — keep the two in sync.
 */
export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: "length",
    label: `${PASSWORD_MIN_LENGTH}+ тэмдэгт`,
    test: (v) => v.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: "uppercase",
    label: "Том үсэг",
    test: (v) => /[A-Z]/.test(v),
  },
  {
    id: "lowercase",
    label: "Жижиг үсэг",
    test: (v) => /[a-z]/.test(v),
  },
  {
    id: "number",
    label: "Тоо",
    test: (v) => /\d/.test(v),
  },
  {
    id: "special",
    label: "Тусгай тэмдэгт",
    test: (v) => /[^A-Za-z0-9\s]/.test(v),
  },
];

export interface PasswordIdentity {
  username?: string;
  email?: string;
  phone?: string;
  code?: string;
  first_name?: string;
  last_name?: string;
  birth_date?: string;
}

const MIN_IDENTITY_FRAGMENT = 4;

const identityFragments = (identity: PasswordIdentity): string[] => {
  const raw = [
    identity.username,
    identity.email,
    identity.phone,
    identity.code,
    identity.first_name,
    identity.last_name,
    identity.birth_date,
    identity.email?.split("@")[0],
    identity.username?.split("@")[0],
    identity.phone?.replace(/\D/g, ""),
    identity.birth_date?.replace(/\D/g, ""),
  ];

  return raw
    .map((part) => part?.trim().toLowerCase() ?? "")
    .filter((part) => part.length >= MIN_IDENTITY_FRAGMENT);
};

// A birth date is not stored on staff accounts, so — as on the backend — the rule is
// enforced on the shape: any 6+ digit run that reads as a date is rejected.
const containsDateLikeRun = (password: string): boolean => {
  const runs = password.match(/\d{6,}/g) ?? [];
  const thisYear = new Date().getFullYear();

  return runs.some((run) => {
    for (let start = 0; start + 4 <= run.length; start += 1) {
      const year = Number(run.slice(start, start + 4));
      if (year >= 1930 && year <= thisYear) return true;
    }
    // yymmdd — no century to check, so validate the month and day instead.
    for (let start = 0; start + 6 <= run.length; start += 1) {
      const month = Number(run.slice(start + 2, start + 4));
      const day = Number(run.slice(start + 4, start + 6));
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) return true;
    }
    return false;
  });
};

/** Returns a Mongolian error message, or undefined when the password is acceptable. */
export const validatePassword = (
  password: string,
  identity: PasswordIdentity = {},
): string | undefined => {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Нууц үг доод тал нь ${PASSWORD_MIN_LENGTH} тэмдэгт байх ёстой`;
  }
  if (!/[A-Z]/.test(password) || !/[a-z]/.test(password)) {
    return "Нууц үг том, жижиг латин үсэг хоёуланг агуулсан байх ёстой";
  }
  if (!/\d/.test(password)) {
    return "Нууц үг доод тал нь 1 тоо агуулсан байх ёстой";
  }
  if (!/[^A-Za-z0-9\s]/.test(password)) {
    return "Нууц үг доод тал нь 1 тусгай тэмдэгт (!@#$%^&* гэх мэт) агуулсан байх ёстой";
  }

  const lowered = password.toLowerCase();
  if (identityFragments(identity).some((part) => lowered.includes(part))) {
    return "Нууц үг нэвтрэх нэр, утас, төрсөн огноо зэрэг хувийн мэдээлэл агуулж болохгүй";
  }
  if (containsDateLikeRun(password)) {
    return "Нууц үг төрсөн огноо мэт тоон дараалал агуулж болохгүй";
  }
  return undefined;
};

/** antd form rule for a new-password field. */
export const passwordRule = (identity: PasswordIdentity = {}) => ({
  validator: (_: unknown, value: string) => {
    if (!value) return Promise.resolve();
    const error = validatePassword(value, identity);
    return error ? Promise.reject(new Error(error)) : Promise.resolve();
  },
});
